<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import preset from './CustomPrimeVuePreset'

const { locale } = useI18n()
const { handleError } = useErrorHandling()
const { updateCandidateLocale, candidateProfile } = useCandidate()
const { updateInternalUserLocale, internalUser } = useInternalUser()
const { addAccessLog } = useAccessLog()
const { currentRoute } = useRouter()
const { init, config } = useFrontendConfiguration()
const head = useLocaleHead({ key: 'id', seo: true })
const { initCountrySetting } = useCountrySetting()

const primary700 = (preset as any).semantic.primary[700]

onErrorCaptured((error) => {
  handleError(error)
  return false
})

onMounted(() => {
  addAccessLog(AccessLogEventType.PAGE_VIEW, uuidv4())
})

watch(() => currentRoute.value.fullPath, () => {
  addAccessLog(AccessLogEventType.PAGE_VIEW, uuidv4())

  if (candidateProfile.value && candidateProfile.value.locale !== locale.value) {
    updateCandidateLocale(locale.value)
  }

  if (internalUser.value && internalUser.value.locale !== locale.value) {
    updateInternalUserLocale(locale.value)
  }
})

await callOnce(async () => {
  await init()

  initCountrySetting(config.value!.ipAddressCountry)

  if (candidateProfile.value && candidateProfile.value.locale !== locale.value) {
    await updateCandidateLocale(locale.value)
  }

  if (internalUser.value && internalUser.value.locale !== locale.value) {
    await updateInternalUserLocale(locale.value)
  }
})
</script>

<template>
  <Html
    :lang="head.htmlAttrs!.lang"
    :dir="head.htmlAttrs!.dir"
    class="notranslate"
    translate="no"
  >
    <Head>
      <Link
        rel="icon"
        href="/favicon.ico"
        sizes="48x48"
      />
      <Link
        rel="icon"
        href="/icon.svg"
        type="image/svg+xml"
      />
      <Link
        rel="apple-touch-icon"
        href="/apple-touch-icon.png"
      />
      <Link
        rel="manifest"
        href="/manifest.webmanifest"
      />
      <template
        v-for="link in head.link"
        :key="link.id"
      >
        <Link
          :id="link.id"
          :rel="link.rel"
          :href="link.href"
          :hreflang="link.hreflang"
        />
      </template>
      <Meta
        name="google"
        content="notranslate"
      />
      <Meta
        name="format-detection"
        content="telephone=no"
      />
      <Meta
        name="theme-color"
        :content="primary700"
      />
      <template
        v-for="meta in head.meta"
        :key="meta.id"
      >
        <Meta
          :id="meta.id"
          :property="meta.property"
          :content="meta.content"
        />
      </template>
    </Head>
    <Body>
      <NuxtPage />
      <Toast />
      <ConfirmDialog />
      <ConsentManagement />
    </Body>
  </Html>
</template>
